import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { ArtworkThumbnail, formatFileSize, isPreviewableImage } from '../components/ArtworkThumbnail';
import { 
    ArrowLeft, Download, Upload, Clock, CheckCircle2, AlertCircle, XCircle,
    Briefcase, FileText, Palette, User, Building2, History,
    Send, Eye, Trash2, File, DollarSign, Plus, X, ShoppingCart, ChevronDown, ChevronRight, Layers,
    MoreHorizontal, ShieldAlert, AlertTriangle, Hourglass
} from 'lucide-react';
import axios from 'axios';
import { artworkJobsApi } from '../lib/api';

import { API_BASE } from '../lib/apiBase';
import { DemoBadge } from '../components/DemoBadge';
const API = API_BASE;

const STATUS_CONFIG = {
    'Open': { label: 'Open', color: 'bg-slate-100 text-slate-700 border-border', icon: Clock },
    'Assigned': { label: 'Assigned', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Briefcase },
    'In_Progress': { label: 'In Progress', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
    'Awaiting_Approval': { label: 'Awaiting Approval', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: AlertCircle },
    'Approved': { label: 'Approved', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
    'Completed': { label: 'Completed', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
};

// Allowed transitions for repro partners
const REPRO_PARTNER_TRANSITIONS = {
    'Assigned': ['In_Progress', 'Awaiting_Approval'],
    'In_Progress': ['Awaiting_Approval'],
    'Awaiting_Approval': [],  // Partner cannot change once submitted for customer approval
    'Approved': [],  // Use the "Submit files to factory" CTA instead of generic status change
};

export default function ReproJobDetailPage() {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const { user, isAdmin } = useAuth();
    const fileInputRef = useRef(null);
    
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [statusComment, setStatusComment] = useState('');
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
    const [uploadComment, setUploadComment] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    // When the partner uploads a corrected version that specifically replaces
    // a customer-uploaded replacement file, we track the source file id so
    // the backend can mark it superseded and link the version chain.
    const [replacingCustomerFileId, setReplacingCustomerFileId] = useState(null);
    const customerReplaceInputRefs = useRef({});

    // Same version-chain mechanism applied to a rejected corrected file
    // (either rejected by customer or via admin override). Selecting a
    // file here threads `replaces_repro_file_id` to the upload endpoint so
    // the old rejected file becomes superseded and the new upload takes
    // its slot as v+1.
    const [replacingReproFileId, setReplacingReproFileId] = useState(null);
    const reproReplaceInputRefs = useRef({});
    
    // Work Performed state
    const [catalogData, setCatalogData] = useState({ catalog: null, items: [] });
    const [workItems, setWorkItems] = useState([]);
    const [isWorkDialogOpen, setIsWorkDialogOpen] = useState(false);
    const [isSubmittingWork, setIsSubmittingWork] = useState(false);
    
    // Artwork rejection state
    const [isRejectArtworkOpen, setIsRejectArtworkOpen] = useState(false);
    const [rejectComment, setRejectComment] = useState('');
    const [isRejectingArtwork, setIsRejectingArtwork] = useState(false);
    const [rejectingFileId, setRejectingFileId] = useState(null);
    const [rejectingFileName, setRejectingFileName] = useState('');

    // Job completion / factory delivery state
    const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
    const [completionNote, setCompletionNote] = useState('');
    const [isCompletingJob, setIsCompletingJob] = useState(false);

    // Admin Artwork Status Rollback (admin/super_admin only) —
    // manually change the review status of a specific artwork or
    // corrected file to recover from customer mistakes.
    const [isAdminOverrideOpen, setIsAdminOverrideOpen] = useState(false);
    const [adminOverrideFile, setAdminOverrideFile] = useState(null); // { id, filename, scope, current_status }
    const [adminOverrideStatus, setAdminOverrideStatus] = useState('');
    const [adminOverrideComment, setAdminOverrideComment] = useState('');
    const [isSubmittingAdminOverride, setIsSubmittingAdminOverride] = useState(false);

    const openAdminOverride = (file, scope) => {
        setAdminOverrideFile({
            id: file.id,
            filename: file.filename,
            scope, // "artwork" or "corrected"
            current_status: file.review_status || 'pending',
        });
        setAdminOverrideStatus('');
        setAdminOverrideComment('');
        setIsAdminOverrideOpen(true);
    };

    const submitAdminOverride = async () => {
        if (!adminOverrideFile || !adminOverrideStatus) return;
        if (adminOverrideStatus === 'rejected' && !adminOverrideComment.trim()) {
            toast.error('A reason is required when marking a file as rejected');
            return;
        }
        setIsSubmittingAdminOverride(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API}/api/repro-jobs/${jobId}/files/${adminOverrideFile.id}/admin-override`,
                {
                    new_status: adminOverrideStatus,
                    comment: adminOverrideComment.trim() || null,
                },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            toast.success(`File status set to "${adminOverrideStatus}"`);
            setIsAdminOverrideOpen(false);
            setAdminOverrideFile(null);
            setAdminOverrideStatus('');
            setAdminOverrideComment('');
            fetchJob();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to override file status');
        } finally {
            setIsSubmittingAdminOverride(false);
        }
    };

    const fetchJob = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/api/repro-jobs/${jobId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setJob(response.data);
        } catch (error) {
            toast.error('Failed to load job');
            navigate('/artwork-jobs');
        } finally {
            setLoading(false);
        }
    }, [jobId, navigate]);

    useEffect(() => {
        fetchJob();
    }, [fetchJob]);

    // Fetch catalog items when job loads
    const fetchCatalogItems = useCallback(async () => {
        if (!job?.partner_id) return;
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/api/repro-jobs/${jobId}/catalog-items`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCatalogData(response.data);
        } catch (error) {
            console.error('Failed to load catalog items:', error);
        }
    }, [jobId, job?.partner_id]);

    useEffect(() => {
        if (job?.partner_id) {
            fetchCatalogItems();
        }
    }, [job?.partner_id, fetchCatalogItems]);

    // Add item to work list
    const addWorkItem = (catalogItem) => {
        const existing = workItems.find(w => w.price_item_id === catalogItem.id);
        if (existing) {
            toast.info('Item already added. Update the quantity instead.');
            return;
        }
        setWorkItems([...workItems, {
            price_item_id: catalogItem.id,
            sku: catalogItem.sku,
            name: catalogItem.name,
            unit: catalogItem.unit,
            cost_price: catalogItem.cost_price,
            quantity: 1
        }]);
    };

    // Update work item quantity
    const updateWorkItemQty = (itemId, qty) => {
        const quantity = parseInt(qty) || 0;
        if (quantity <= 0) {
            removeWorkItem(itemId);
            return;
        }
        setWorkItems(workItems.map(w => 
            w.price_item_id === itemId ? { ...w, quantity } : w
        ));
    };

    // Remove work item
    const removeWorkItem = (itemId) => {
        setWorkItems(workItems.filter(w => w.price_item_id !== itemId));
    };

    // Submit work performed
    const submitWorkPerformed = async () => {
        if (workItems.length === 0) {
            toast.error('Please add at least one item');
            return;
        }
        
        setIsSubmittingWork(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API}/api/repro-jobs/${jobId}/work-performed`, {
                items: workItems.map(w => ({
                    price_item_id: w.price_item_id,
                    quantity: w.quantity
                }))
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success('Work performed submitted successfully');
            setIsWorkDialogOpen(false);
            setWorkItems([]);
            fetchJob();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit work performed');
        } finally {
            setIsSubmittingWork(false);
        }
    };

    // Calculate work total
    const workTotal = workItems.reduce((sum, item) => sum + (item.cost_price * item.quantity), 0);

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Open dialog to add comment. `replacingCustomerFileId` is set by
        // the "Upload corrected version" button on a customer replacement
        // card; the generic "Upload Corrected File" button leaves it null.
        setSelectedFile(file);
        setUploadComment('');
        setIsUploadDialogOpen(true);
    };

    const handleCustomerReplaceUpload = (customerFileId) => (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setReplacingCustomerFileId(customerFileId);
        setSelectedFile(file);
        setUploadComment('');
        setIsUploadDialogOpen(true);
    };

    const handleReproReplaceUpload = (reproFileId) => (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setReplacingReproFileId(reproFileId);
        setSelectedFile(file);
        setUploadComment('');
        setIsUploadDialogOpen(true);
    };

    const submitFileUpload = async () => {
        if (!selectedFile) return;

        setUploading(true);
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('description', 'Corrected artwork');
            if (uploadComment.trim()) {
                formData.append('comment', uploadComment.trim());
            }
            if (replacingCustomerFileId) {
                formData.append('replaces_customer_file_id', replacingCustomerFileId);
            }
            if (replacingReproFileId) {
                formData.append('replaces_repro_file_id', replacingReproFileId);
            }

            await axios.post(`${API}/api/repro-jobs/${jobId}/upload`, formData, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            toast.success('File uploaded successfully');
            setIsUploadDialogOpen(false);
            setSelectedFile(null);
            setUploadComment('');
            setReplacingCustomerFileId(null);
            setReplacingReproFileId(null);
            fetchJob();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload file');
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            // Also reset any per-customer-file refs so re-selecting the
            // same file works on subsequent uploads.
            Object.values(customerReplaceInputRefs.current || {}).forEach((el) => {
                if (el) el.value = '';
            });
            Object.values(reproReplaceInputRefs.current || {}).forEach((el) => {
                if (el) el.value = '';
            });
        }
    };

    const handleDownload = async (fileId, filename) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API}/api/repro-jobs/${jobId}/download/${fileId}`, {
                method: 'GET',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                }
            });
            
            const contentType = response.headers.get('content-type');
            
            if (contentType && contentType.includes('application/json')) {
                // S3 file - response contains presigned URL
                const data = await response.json();
                if (data.download_url) {
                    window.open(data.download_url, '_blank');
                } else {
                    throw new Error('No download URL in response');
                }
            } else if (response.ok) {
                // Local file - download as blob
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } else {
                throw new Error('Download failed');
            }
        } catch (error) {
            console.error('Download error:', error);
            toast.error('Failed to download file');
        }
    };

    const handleStatusUpdate = async () => {
        if (!newStatus) return;

        setIsUpdatingStatus(true);
        try {
            const token = localStorage.getItem('token');
            await axios.patch(
                `${API}/api/repro-jobs/${jobId}/status?status=${newStatus}${statusComment ? `&comment=${encodeURIComponent(statusComment)}` : ''}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success('Status updated');
            setIsStatusDialogOpen(false);
            setNewStatus('');
            setStatusComment('');
            fetchJob();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update status');
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleRejectArtwork = async () => {
        if (!rejectComment.trim()) {
            toast.error('Please provide a reason for rejection');
            return;
        }
        setIsRejectingArtwork(true);
        try {
            if (rejectingFileId) {
                // Per-file rejection
                await artworkJobsApi.rejectFile(jobId, rejectingFileId, rejectComment.trim());
                toast.success(`New file requested for ${rejectingFileName}`);
            } else {
                // Bulk rejection (all pending files)
                await artworkJobsApi.rejectArtwork(jobId, rejectComment.trim());
                toast.success('New files requested — customer will be notified');
            }
            setIsRejectArtworkOpen(false);
            setRejectComment('');
            setRejectingFileId(null);
            setRejectingFileName('');
            fetchJob();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reject');
        } finally {
            setIsRejectingArtwork(false);
        }
    };

    const handleApproveArtwork = async () => {
        try {
            await artworkJobsApi.approveArtwork(jobId);
            toast.success('Artwork approved');
            fetchJob();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to approve artwork');
        }
    };

    const handleCompleteJob = async () => {
        if (!job?.work_performed || job.work_performed.length === 0) {
            toast.error('Please add Work Performed items before marking as delivered');
            return;
        }
        setIsCompletingJob(true);
        try {
            await artworkJobsApi.completeJob(jobId, completionNote.trim() || null);
            toast.success('Marked as delivered to factory — job completed');
            setIsCompleteDialogOpen(false);
            setCompletionNote('');
            fetchJob();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to complete job');
        } finally {
            setIsCompletingJob(false);
        }
    };

    const [isUnassigning, setIsUnassigning] = useState(false);
    const handleUnassignJob = async () => {
        if (!job?.partner_id) return;
        const partnerName = job.partner_name || 'the current partner';
        const confirmed = window.confirm(
            `Unassign this job from ${partnerName}?\n\nThe job will return to the initial queue and the partner assignment will be cleared. This is only possible while no corrected artwork has been uploaded.`
        );
        if (!confirmed) return;
        setIsUnassigning(true);
        try {
            await artworkJobsApi.unassign(jobId);
            toast.success('Job unassigned — returned to initial queue');
            fetchJob();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to unassign job');
        } finally {
            setIsUnassigning(false);
        }
    };

    const [isResetting, setIsResetting] = useState(false);
    const handleResetToMissing = async () => {
        const confirmed = window.confirm(
            'Reset Artwork to Missing?\n\nThis cancels the current artwork job entirely, returns the order to the "Orders missing artwork" queue, and clears the assignment. Uploaded files stay in storage but no longer drive the workflow. Only possible while no corrected artwork has been uploaded.\n\nContinue?'
        );
        if (!confirmed) return;
        setIsResetting(true);
        try {
            await artworkJobsApi.resetToMissing(jobId);
            toast.success('Artwork workflow reset to Missing');
            navigate('/artwork-jobs');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reset artwork');
            setIsResetting(false);
        }
    };

    const getAvailableStatusTransitions = () => {
        if (!job) return [];
        
        if (isAdmin) {
            // Admins can set any status
            return Object.keys(STATUS_CONFIG);
        } else {
            // Repro partners can only do certain transitions
            return REPRO_PARTNER_TRANSITIONS[job.status] || [];
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <main className="container mx-auto px-4 py-6 max-w-5xl">
                    <div className="text-center py-12 text-slate-500">Loading job...</div>
                </main>
            </div>
        );
    }

    if (!job) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <main className="container mx-auto px-4 py-6 max-w-5xl">
                    <div className="text-center py-12 text-slate-500">Job not found</div>
                </main>
            </div>
        );
    }

    const statusConfig = STATUS_CONFIG[job.status] || STATUS_CONFIG['Open'];
    const StatusIcon = statusConfig.icon;
    const availableTransitions = getAvailableStatusTransitions();

    // Partner-facing next action, computed server-side from the file
    // review statuses. Rendered as a SECOND badge next to the job
    // status so "Waiting for customer file" is never mistaken for a
    // partner action.
    const VERB_STYLE = {
        review_new_customer_file:      { color: 'bg-rose-50 text-rose-700 border-rose-200',   icon: AlertTriangle },
        address_change_request:        { color: 'bg-rose-50 text-rose-700 border-rose-200',   icon: AlertTriangle },
        upload_corrected_file:         { color: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertTriangle },
        submit_for_customer_approval:  { color: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertTriangle },
        deliver_to_factory:            { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
        waiting_for_customer_file:     { color: 'bg-sky-50 text-sky-700 border-sky-200',      icon: Hourglass },
        waiting_for_customer_approval: { color: 'bg-sky-50 text-sky-700 border-sky-200',      icon: Hourglass },
    };
    const verbStyle = job.next_action ? VERB_STYLE[job.next_action] : null;
    const VerbIcon = verbStyle?.icon;
    // Per-file state map so file rows can badge themselves individually.
    const fileStateById = Object.fromEntries((job.file_states || []).map(fs => [fs.file_id, fs]));

    return (
        <div className="min-h-screen bg-background">
            <Header />
            <main className="container mx-auto px-4 py-6 max-w-5xl">
                {/* Back Button & Header */}
                <div className="mb-6">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/artwork-jobs')} className="mb-4">
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Back to Artwork Jobs
                    </Button>
                    
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                                <h1 className="text-2xl font-bold text-slate-900">{job.order_title}</h1>
                                <Badge className={`${statusConfig.color} gap-1 px-3 py-1`}>
                                    <StatusIcon className="h-4 w-4" />
                                    {statusConfig.label}
                                </Badge>
                                {verbStyle && (
                                    <Badge
                                        variant="outline"
                                        className={`${verbStyle.color} gap-1 px-3 py-1`}
                                        title={job.next_action_label}
                                        data-testid={`detail-verb-${job.next_action}`}
                                    >
                                        <VerbIcon className="h-4 w-4" />
                                        {job.next_action_label}
                                    </Badge>
                                )}
                                <DemoBadge order={job} size="md" />
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-500">
                                <span className="font-mono">#{String(job.order_number).padStart(6, '0')}</span>
                                {job.catalog_number && <span>Cat: {job.catalog_number}</span>}
                                <span className="flex items-center gap-1">
                                    <Building2 className="h-3 w-3" />
                                    {job.organization_name || job.customer_name}
                                </span>
                            </div>
                        </div>
                        
                        {availableTransitions.length > 0 && (
                            <Button onClick={() => setIsStatusDialogOpen(true)} data-testid="update-status-btn">
                                Update Status
                            </Button>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content - 2 columns */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* === Final Stage block (Approved/Completed): rendered at top so it's the primary action === */}
                        {['Approved', 'Completed'].includes(job.status) && (
                            <>
                                {/* Final Completion / Mark as Delivered to Factory — only when customer approved */}
                                {job.status === 'Approved' && (
                                    <Card className="border-emerald-300 bg-emerald-50/30" data-testid="deliver-to-factory-card">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="text-base flex items-center gap-2 text-emerald-800">
                                                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                                                Customer Approved — Final Step
                                            </CardTitle>
                                            <CardDescription className="text-emerald-700">
                                                Confirm that you have delivered the approved files to the factory. Add Work Performed below first.
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <Button
                                                size="lg"
                                                className="w-full bg-emerald-600 hover:bg-emerald-700"
                                                onClick={() => setIsCompleteDialogOpen(true)}
                                                disabled={!job.work_performed || job.work_performed.length === 0}
                                                data-testid="submit-to-factory-btn"
                                            >
                                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                                Mark as Delivered to Factory
                                            </Button>
                                            {(!job.work_performed || job.work_performed.length === 0) && (
                                                <p className="text-xs text-amber-700 mt-2 text-center">
                                                    Add Work Performed below before marking as delivered.
                                                </p>
                                            )}
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Completed — show delivery note */}
                                {job.status === 'Completed' && (job.factory_delivery_note || job.delivered_to_factory_at) && (
                                    <Card className="border-emerald-200 bg-emerald-50/30" data-testid="factory-delivery-card">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="text-base flex items-center gap-2 text-emerald-800">
                                                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                                                Delivered to Factory
                                            </CardTitle>
                                            <CardDescription className="text-emerald-700">
                                                {job.delivered_to_factory_at && (
                                                    <>By {job.delivered_to_factory_by_name || '—'} • {formatDate(job.delivered_to_factory_at)}</>
                                                )}
                                            </CardDescription>
                                        </CardHeader>
                                        {job.factory_delivery_note && (
                                            <CardContent>
                                                <Label className="text-xs text-emerald-700">Delivery Note</Label>
                                                <p className="text-sm text-slate-700 whitespace-pre-wrap mt-1">{job.factory_delivery_note}</p>
                                            </CardContent>
                                        )}
                                    </Card>
                                )}

                                {/* Work Performed — required for completion */}
                                <Card className="border-amber-200 bg-amber-50/30">
                                    <CardHeader className="pb-2">
                                        <div className="flex items-center justify-between">
                                            <CardTitle className="text-base flex items-center gap-2">
                                                <ShoppingCart className="h-4 w-4 text-amber-600" />
                                                Work Performed
                                            </CardTitle>
                                            {job.work_performed?.length > 0 && (
                                                <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                                                    {job.work_performed.length} item{job.work_performed.length !== 1 ? 's' : ''}
                                                </Badge>
                                            )}
                                        </div>
                                        <CardDescription>
                                            {job.status === 'Approved'
                                                ? 'Required before marking as delivered to factory'
                                                : 'Pricing items recorded for this job'}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        {job.work_performed?.length > 0 ? (
                                            <div className="space-y-3 mb-4">
                                                <div className="rounded-lg border border-amber-200 overflow-hidden">
                                                    <table className="w-full text-sm">
                                                        <thead className="bg-amber-100/50">
                                                            <tr>
                                                                <th className="text-left px-3 py-2 font-medium">Item</th>
                                                                <th className="text-right px-3 py-2 font-medium">Qty</th>
                                                                <th className="text-right px-3 py-2 font-medium">Price</th>
                                                                <th className="text-right px-3 py-2 font-medium">Total</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {job.work_performed.map((item, idx) => (
                                                                <tr key={item.id || idx} className="border-t border-amber-100">
                                                                    <td className="px-3 py-2">
                                                                        <div>
                                                                            <span className="font-medium">{item.name}</span>
                                                                            <span className="text-xs text-slate-400 ml-2">({item.sku})</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-3 py-2 text-right">{item.quantity} {item.unit}</td>
                                                                    <td className="px-3 py-2 text-right text-slate-500">{item.cost_price?.toFixed(2)}</td>
                                                                    <td className="px-3 py-2 text-right font-medium">{item.line_total?.toFixed(2)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot className="bg-amber-100/30">
                                                            <tr className="border-t border-amber-200">
                                                                <td colSpan="3" className="px-3 py-2 text-right font-semibold">Total:</td>
                                                                <td className="px-3 py-2 text-right font-bold text-amber-700">
                                                                    {job.work_performed_total?.toFixed(2)} {job.work_performed_currency}
                                                                </td>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                </div>
                                                {job.status === 'Approved' && (
                                                    <Button 
                                                        variant="outline" 
                                                        className="w-full border-amber-300 hover:bg-amber-50"
                                                        onClick={() => {
                                                            setWorkItems(job.work_performed.map(w => ({
                                                                price_item_id: w.price_item_id,
                                                                sku: w.sku,
                                                                name: w.name,
                                                                unit: w.unit,
                                                                cost_price: w.cost_price,
                                                                quantity: w.quantity
                                                            })));
                                                            setIsWorkDialogOpen(true);
                                                        }}
                                                        data-testid="edit-work-performed-btn"
                                                    >
                                                        Edit Work Performed
                                                    </Button>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="text-center py-4">
                                                {job.status === 'Approved' ? (
                                                    <>
                                                        <p className="text-sm text-slate-500 mb-3">
                                                            No work performed items added yet.
                                                            <br />
                                                            <span className="text-amber-600 font-medium">Required before marking as delivered to factory.</span>
                                                        </p>
                                                        {catalogData.catalog ? (
                                                            <Button 
                                                                variant="outline" 
                                                                className="border-amber-300 hover:bg-amber-50"
                                                                onClick={() => setIsWorkDialogOpen(true)}
                                                                data-testid="add-work-performed-btn"
                                                            >
                                                                <Plus className="h-4 w-4 mr-2" />
                                                                Add Work Performed
                                                            </Button>
                                                        ) : (
                                                            <p className="text-xs text-slate-400">
                                                                {catalogData.message || 'No price catalog assigned to this partner'}
                                                            </p>
                                                        )}
                                                    </>
                                                ) : (
                                                    <p className="text-sm text-slate-400">No work items recorded</p>
                                                )}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </>
                        )}

                        {/* Instructions */}
                        {job.instructions && (
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base">Instructions</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{job.instructions}</p>
                                </CardContent>
                            </Card>
                        )}

                        {/* Vinyl Specs */}
                        {job.vinyl_specs && (
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base">Vinyl Specifications</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                        {job.vinyl_specs.format && (
                                            <div>
                                                <Label className="text-xs text-slate-400">Format</Label>
                                                <p className="font-medium">{job.vinyl_specs.format}</p>
                                            </div>
                                        )}
                                        {job.vinyl_specs.speed && (
                                            <div>
                                                <Label className="text-xs text-slate-400">Speed</Label>
                                                <p className="font-medium">{job.vinyl_specs.speed}</p>
                                            </div>
                                        )}
                                        {job.vinyl_specs.color && (
                                            <div>
                                                <Label className="text-xs text-slate-400">Color</Label>
                                                <p className="font-medium">{job.vinyl_specs.color}</p>
                                            </div>
                                        )}
                                        {job.vinyl_specs.quantity && (
                                            <div>
                                                <Label className="text-xs text-slate-400">Quantity</Label>
                                                <p className="font-medium">{job.vinyl_specs.quantity}</p>
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Artwork Files (from customer) — Per-File Review */}
                        <Card>
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Palette className="h-4 w-4 text-purple-500" />
                                        Original Artwork
                                    </CardTitle>
                                    <div className="flex items-center gap-2">
                                        {job.artwork_status === 'rejected' && (
                                            <Badge className="bg-red-100 text-red-700">
                                                New file requested
                                            </Badge>
                                        )}
                                        <Badge variant="outline">{(job.artwork_files || []).filter(f => !f.superseded).length} files</Badge>
                                    </div>
                                </div>
                                <CardDescription>Review files and request corrections if needed</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {(job.artwork_files || []).filter(f => !f.superseded).length > 0 ? (
                                    <div className="space-y-3">
                                        {(job.artwork_files || []).filter(f => !f.superseded).map((file) => {
                                            const status = file.review_status || 'pending';
                                            const canReviewFile = !['Approved', 'Completed'].includes(job.status);
                                            const fileAction = fileStateById[file.id];
                                            const fileVerb = fileAction?.next_action;
                                            const fileVerbStyle = fileVerb ? VERB_STYLE[fileVerb] : null;
                                            const FileVerbIcon = fileVerbStyle?.icon;
                                            return (
                                                <div key={file.id} className={`p-3 rounded-lg border ${
                                                    status === 'rejected' ? 'border-red-200 bg-red-50/30' :
                                                    'border-slate-200 bg-background'
                                                }`} data-testid={`artwork-file-${file.id}`}>
                                                    <div className="flex items-center justify-between gap-3 min-w-0">
                                                        <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
                                                            <ArtworkThumbnail 
                                                                file={file} 
                                                                size="md"
                                                                onClick={() => handleDownload(file.id, file.filename)}
                                                                fallbackColor="text-purple-400"
                                                                fallbackBg="bg-purple-50"
                                                            />
                                                            <div className="min-w-0 flex-1 overflow-hidden">
                                                                <p className="text-sm font-medium truncate" title={file.filename}>{file.filename}</p>
                                                                <p className="text-xs text-slate-400">
                                                                    {formatFileSize(file.size)}
                                                                    {file.version > 1 && <span className="ml-1 text-indigo-500">(v{file.version})</span>}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            {fileVerbStyle && (
                                                                <Badge
                                                                    variant="outline"
                                                                    className={`${fileVerbStyle.color} gap-1 whitespace-nowrap`}
                                                                    title={fileAction.next_action_label}
                                                                    data-testid={`file-verb-${file.id}`}
                                                                >
                                                                    <FileVerbIcon className="h-3 w-3" />
                                                                    {fileAction.next_action_label}
                                                                </Badge>
                                                            )}
                                                            {status === 'rejected' ? (
                                                                <Badge className="bg-red-100 text-red-700" data-testid={`file-status-${file.id}`}>
                                                                    New file requested
                                                                </Badge>
                                                            ) : canReviewFile ? (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="h-7 text-xs border-red-200 text-red-700 hover:bg-red-50"
                                                                    onClick={() => {
                                                                        setRejectingFileId(file.id);
                                                                        setRejectingFileName(file.filename);
                                                                        setRejectComment('');
                                                                        setIsRejectArtworkOpen(true);
                                                                    }}
                                                                    data-testid={`request-new-file-${file.id}`}
                                                                >
                                                                    <XCircle className="h-3 w-3 mr-1" /> Request new file
                                                                </Button>
                                                            ) : null}
                                                            <Button variant="ghost" size="sm" onClick={() => handleDownload(file.id, file.filename)}>
                                                                <Download className="h-4 w-4" />
                                                            </Button>
                                                            {isAdmin && (
                                                                <DropdownMenu>
                                                                    <DropdownMenuTrigger asChild>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-7 w-7 p-0"
                                                                            data-testid={`admin-actions-artwork-${file.id}`}
                                                                            title="Admin actions"
                                                                        >
                                                                            <MoreHorizontal className="h-4 w-4" />
                                                                        </Button>
                                                                    </DropdownMenuTrigger>
                                                                    <DropdownMenuContent align="end" className="w-56">
                                                                        <DropdownMenuLabel className="flex items-center gap-2 text-xs">
                                                                            <ShieldAlert className="h-3.5 w-3.5 text-amber-600" />
                                                                            Admin Actions
                                                                        </DropdownMenuLabel>
                                                                        <DropdownMenuSeparator />
                                                                        <DropdownMenuItem
                                                                            onClick={() => openAdminOverride(file, 'artwork')}
                                                                            data-testid={`admin-override-artwork-${file.id}`}
                                                                        >
                                                                            Override file status…
                                                                        </DropdownMenuItem>
                                                                    </DropdownMenuContent>
                                                                </DropdownMenu>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {/* Rejection comment */}
                                                    {status === 'rejected' && file.rejection_comment && (
                                                        <div className="mt-2 p-2 bg-red-100 rounded text-xs text-red-700">
                                                            <span className="font-medium">Feedback:</span> {file.rejection_comment}
                                                        </div>
                                                    )}
                                                    {/* Version history */}
                                                    {file.version > 1 && (() => {
                                                        const prevVersions = (job.artwork_files || []).filter(f => f.superseded && f.id === file.replaces_file_id);
                                                        if (prevVersions.length === 0) return null;
                                                        return (
                                                            <details className="mt-2 pt-2 border-t border-slate-100">
                                                                <summary className="text-[10px] text-slate-400 cursor-pointer hover:text-slate-600">
                                                                    Previous version{prevVersions.length > 1 ? 's' : ''}
                                                                </summary>
                                                                <div className="mt-1 space-y-1">
                                                                    {prevVersions.map(pv => (
                                                                        <div key={pv.id} className="flex items-center gap-2 p-1.5 bg-slate-50 rounded text-[10px] text-slate-400 opacity-60">
                                                                            <File className="h-3 w-3" />
                                                                            <span className="line-through">{pv.filename}</span>
                                                                            <span>(v{pv.version || 1})</span>
                                                                            {pv.rejection_comment && <span className="text-red-400 truncate max-w-[150px]">{pv.rejection_comment}</span>}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </details>
                                                        );
                                                    })()}
                                                </div>
                                            );
                                        })}
                                        
                                        {/* Download All Files (streamed ZIP) */}
                                        {(job.artwork_files || []).filter(f => !f.superseded).length > 1 && (
                                            <div className="pt-3 border-t mt-2">
                                                <Button 
                                                    variant="outline"
                                                    className="w-full"
                                                    size="sm"
                                                    onClick={() => {
                                                        // Backend streams a ZIP (STORE / no
                                                        // compression) so the browser starts
                                                        // saving immediately even for 900 MB+
                                                        // artwork sets. JWT travels as a query
                                                        // param so window.open can fire the
                                                        // download without juggling Blob URLs
                                                        // (which would buffer the whole ZIP
                                                        // into RAM and defeat streaming).
                                                        const token = localStorage.getItem('token');
                                                        const url = `${process.env.REACT_APP_BACKEND_URL}/api/repro-jobs/${job.id}/download-all?token=${encodeURIComponent(token)}`;
                                                        const files = (job.artwork_files || []).filter(f => !f.superseded);
                                                        toast.info(`Preparing ZIP of ${files.length} files…`);
                                                        // Use a hidden iframe so the page doesn't
                                                        // navigate away while the ZIP streams.
                                                        const iframe = document.createElement('iframe');
                                                        iframe.style.display = 'none';
                                                        iframe.src = url;
                                                        document.body.appendChild(iframe);
                                                        // Remove after a delay — long enough for
                                                        // the Content-Disposition header to land
                                                        // and the browser to attach the download.
                                                        setTimeout(() => {
                                                            try { document.body.removeChild(iframe); } catch (_e) { /* noop */ }
                                                        }, 60_000);
                                                    }}
                                                    data-testid="download-all-artwork-btn"
                                                >
                                                    <Download className="h-4 w-4 mr-2" />
                                                    Download All Files
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-sm text-slate-400 text-center py-4">No artwork files uploaded yet</p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Customer-uploaded replacement files — pending
                            repro work. Surfaced separately so the partner
                            never mistakes them for "approved corrected
                            artwork". Each card has its own "Upload corrected
                            version" action which threads `replaces_customer_file_id`
                            to the upload endpoint. */}
                        {(job.repro_files || []).some(
                            (f) => f.source === 'customer_replacement'
                                && !f.superseded
                                && f.review_status === 'awaiting_repro',
                        ) && (
                            <Card className="border-amber-200 bg-amber-50/30" data-testid="customer-replacement-section">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Upload className="h-4 w-4 text-amber-600" />
                                        Customer Replacement Files
                                    </CardTitle>
                                    <CardDescription>
                                        The customer uploaded new artwork. Download, apply any
                                        needed corrections, then upload a new corrected version
                                        back to the customer.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-2">
                                        {(job.repro_files || [])
                                            .filter(
                                                (f) => f.source === 'customer_replacement'
                                                    && !f.superseded
                                                    && f.review_status === 'awaiting_repro',
                                            )
                                            .map((file) => (
                                                <div
                                                    key={file.id}
                                                    className="p-3 rounded-lg border border-amber-200 bg-white"
                                                    data-testid={`customer-replacement-file-${file.id}`}
                                                >
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <ArtworkThumbnail
                                                                file={file}
                                                                size="md"
                                                                onClick={() => handleDownload(file.id, file.filename)}
                                                                fallbackColor="text-amber-500"
                                                                fallbackBg="bg-amber-100"
                                                            />
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-medium truncate flex items-center gap-2">
                                                                    <span className="truncate">{file.filename}</span>
                                                                    <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">
                                                                        Customer upload
                                                                    </Badge>
                                                                </p>
                                                                <p className="text-xs text-slate-400">
                                                                    {formatFileSize(file.size)} · Uploaded by {file.uploaded_by_name || 'Customer'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleDownload(file.id, file.filename)}
                                                                data-testid={`customer-replacement-download-${file.id}`}
                                                            >
                                                                <Download className="h-4 w-4 mr-1" />
                                                                Download
                                                            </Button>
                                                            <input
                                                                type="file"
                                                                ref={(el) => (customerReplaceInputRefs.current[file.id] = el)}
                                                                onChange={handleCustomerReplaceUpload(file.id)}
                                                                className="hidden"
                                                                accept=".pdf,.ai,.eps,.psd,.png,.jpg,.jpeg,.tiff,.tif"
                                                            />
                                                            <Button
                                                                size="sm"
                                                                onClick={() => customerReplaceInputRefs.current[file.id]?.click()}
                                                                disabled={uploading}
                                                                data-testid={`upload-corrected-against-customer-${file.id}`}
                                                            >
                                                                <Upload className="h-4 w-4 mr-1" />
                                                                Upload corrected version
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Repro Files (corrected artwork) */}
                        <Card>
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <FileText className="h-4 w-4 text-blue-500" />
                                        Corrected Artwork
                                    </CardTitle>
                                    <Badge variant="outline">{job.repro_files?.length || 0} files</Badge>
                                </div>
                                <CardDescription>Files uploaded by repro partner</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {(() => {
                                    // Customer-uploaded replacements awaiting repro work live in
                                    // their own card above — don't render them here so the partner
                                    // doesn't confuse them with finished corrected artwork.
                                    const partnerCorrectedFiles = (job.repro_files || []).filter(
                                        (f) => !(f.source === 'customer_replacement'
                                                 && !f.superseded
                                                 && f.review_status === 'awaiting_repro'),
                                    );
                                    return partnerCorrectedFiles.length > 0 ? (
                                    <div className="space-y-2 mb-4">
                                        {partnerCorrectedFiles.map((file) => {
                                            // Per-file review status surfaced from the new
                                            // approve/reject/customer-replace endpoints. Repro partner
                                            // needs this to decide what to act on next: a "rejected"
                                            // file with feedback means "upload a new corrected version".
                                            const reviewStatus = file.review_status;
                                            const isApproved = reviewStatus === 'approved';
                                            const isRejected = reviewStatus === 'rejected';
                                            const isSuperseded = !!file.superseded;
                                            const isCustomerReplacement = file.source === 'customer_replacement';
                                            const fileAction = fileStateById[file.id];
                                            const fileVerb = !isSuperseded ? fileAction?.next_action : null;
                                            const fileVerbStyle = fileVerb ? VERB_STYLE[fileVerb] : null;
                                            const FileVerbIcon = fileVerbStyle?.icon;
                                            const cardClass = isSuperseded
                                                ? 'bg-slate-50 border-slate-200 opacity-70'
                                                : isApproved
                                                ? 'bg-emerald-50 border-emerald-200'
                                                : isRejected
                                                ? 'bg-red-50 border-red-200'
                                                : 'bg-blue-50 border-blue-100';
                                            return (
                                                <div
                                                    key={file.id}
                                                    className={`p-3 rounded-lg border ${cardClass}`}
                                                    data-testid={`repro-corrected-${file.id}`}
                                                >
                                                    <div className="flex items-center justify-between gap-3 min-w-0">
                                                        <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
                                                            <ArtworkThumbnail
                                                                file={file}
                                                                size="md"
                                                                onClick={() => handleDownload(file.id, file.filename)}
                                                                fallbackColor={
                                                                    isApproved
                                                                        ? 'text-emerald-500'
                                                                        : isRejected
                                                                        ? 'text-red-500'
                                                                        : 'text-blue-400'
                                                                }
                                                                fallbackBg={
                                                                    isApproved
                                                                        ? 'bg-emerald-100'
                                                                        : isRejected
                                                                        ? 'bg-red-100'
                                                                        : 'bg-blue-100'
                                                                }
                                                            />
                                                            <div className="min-w-0 flex-1 overflow-hidden">
                                                                <p className="text-sm font-medium truncate flex items-center gap-2 min-w-0" title={file.filename}>
                                                                    <span className="truncate min-w-0 max-w-full">{file.filename}</span>
                                                                    {file.version > 1 && (
                                                                        <span className="bg-indigo-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0">
                                                                            v{file.version}
                                                                        </span>
                                                                    )}
                                                                    {isCustomerReplacement && (
                                                                        <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] flex-shrink-0">
                                                                            Customer upload
                                                                        </Badge>
                                                                    )}
                                                                    {isApproved && (
                                                                        <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] flex-shrink-0">
                                                                            Approved
                                                                        </Badge>
                                                                    )}
                                                                    {isRejected && (
                                                                        <Badge className="bg-red-100 text-red-700 border-0 text-[10px] flex-shrink-0">
                                                                            Changes requested
                                                                        </Badge>
                                                                    )}
                                                                    {fileVerbStyle && (
                                                                        <Badge
                                                                            variant="outline"
                                                                            className={`${fileVerbStyle.color} gap-1 text-[10px] flex-shrink-0 whitespace-nowrap`}
                                                                            title={fileAction.next_action_label}
                                                                            data-testid={`file-verb-${file.id}`}
                                                                        >
                                                                            <FileVerbIcon className="h-2.5 w-2.5" />
                                                                            {fileAction.next_action_label}
                                                                        </Badge>
                                                                    )}
                                                                    {isSuperseded && (
                                                                        <Badge variant="outline" className="text-[10px] text-slate-500 flex-shrink-0">
                                                                            superseded
                                                                        </Badge>
                                                                    )}
                                                                </p>
                                                                <p className="text-xs text-slate-400 truncate">
                                                                    {formatFileSize(file.size)} • Uploaded by {file.uploaded_by_name || 'Unknown'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="flex-shrink-0"
                                                            onClick={() => handleDownload(file.id, file.filename)}
                                                            data-testid={`repro-corrected-download-${file.id}`}
                                                        >
                                                            <Download className="h-4 w-4 mr-1" />
                                                            Download
                                                        </Button>
                                                        {isAdmin && !isSuperseded && (
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-7 w-7 p-0 flex-shrink-0"
                                                                        data-testid={`admin-actions-corrected-${file.id}`}
                                                                        title="Admin actions"
                                                                    >
                                                                        <MoreHorizontal className="h-4 w-4" />
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end" className="w-56">
                                                                    <DropdownMenuLabel className="flex items-center gap-2 text-xs">
                                                                        <ShieldAlert className="h-3.5 w-3.5 text-amber-600" />
                                                                        Admin Actions
                                                                    </DropdownMenuLabel>
                                                                    <DropdownMenuSeparator />
                                                                    <DropdownMenuItem
                                                                        onClick={() => openAdminOverride(file, 'corrected')}
                                                                        data-testid={`admin-override-corrected-${file.id}`}
                                                                    >
                                                                        Override file status…
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        )}
                                                    </div>
                                                    {isRejected && file.review_comment && (
                                                        <div
                                                            className="mt-3 p-2.5 bg-white border border-red-200 rounded text-sm text-slate-700 whitespace-pre-line"
                                                            data-testid={`repro-corrected-feedback-${file.id}`}
                                                        >
                                                            <span className="text-xs font-semibold text-red-700 block mb-1">
                                                                Customer feedback ({file.reviewed_by_name || 'Customer'}):
                                                            </span>
                                                            {file.review_comment}
                                                            <p className="text-[11px] text-red-500 mt-1.5">
                                                                Use <strong>Upload new version</strong> to replace this file with a corrected one in the same version chain.
                                                            </p>
                                                        </div>
                                                    )}

                                                    {/* Replace / Upload new version — rejected active corrected files
                                                        can be replaced in-place so the version chain stays consolidated
                                                        (v+1 replaces the rejected file, the old file becomes superseded). */}
                                                    {isRejected && !isSuperseded && (
                                                        <div className="mt-3 flex items-center justify-end">
                                                            <input
                                                                type="file"
                                                                ref={(el) => (reproReplaceInputRefs.current[file.id] = el)}
                                                                onChange={handleReproReplaceUpload(file.id)}
                                                                className="hidden"
                                                                accept=".pdf,.ai,.eps,.psd,.png,.jpg,.jpeg,.tiff,.tif"
                                                            />
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => reproReplaceInputRefs.current[file.id]?.click()}
                                                                disabled={uploading}
                                                                className="border-red-200 text-red-700 hover:bg-red-50"
                                                                data-testid={`upload-new-version-${file.id}`}
                                                            >
                                                                <Upload className="h-3.5 w-3.5 mr-1" />
                                                                Upload new version
                                                            </Button>
                                                        </div>
                                                    )}

                                                    {/* Previous versions for a corrected file's chain. Resolves any
                                                        superseded repro_file that ultimately points at this file's
                                                        id via `replaces_file_id` (direct predecessor). */}
                                                    {file.version > 1 && (() => {
                                                        const prevVersions = (job.repro_files || []).filter(
                                                            (f) => f.superseded && f.id === file.replaces_file_id,
                                                        );
                                                        if (prevVersions.length === 0) return null;
                                                        return (
                                                            <details className="mt-2 pt-2 border-t border-slate-100">
                                                                <summary className="text-[10px] text-slate-400 cursor-pointer hover:text-slate-600">
                                                                    Previous version{prevVersions.length > 1 ? 's' : ''}
                                                                </summary>
                                                                <div className="mt-1 space-y-1">
                                                                    {prevVersions.map((pv) => (
                                                                        <div key={pv.id} className="flex items-center gap-2 p-1.5 bg-slate-50 rounded text-[10px] text-slate-400 opacity-70">
                                                                            <File className="h-3 w-3" />
                                                                            <span className="line-through">{pv.filename}</span>
                                                                            <span>(v{pv.version || 1})</span>
                                                                            {pv.review_comment && (
                                                                                <span className="text-red-400 truncate max-w-[180px]" title={pv.review_comment}>
                                                                                    {pv.review_comment}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </details>
                                                        );
                                                    })()}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p className="text-sm text-slate-400 text-center py-4 mb-4">No corrected files uploaded yet</p>
                                );
                                })()}
                                
                                {/* Upload Button — allowed whenever the job
                                    isn't final OR when at least one corrected
                                    file is rejected OR when a customer-replacement
                                    file is awaiting repro work (so the partner can
                                    submit a new version mid-approval cycle). */}
                                {(!['Approved', 'Completed'].includes(job.status) &&
                                  (job.status !== 'Awaiting_Approval' ||
                                   (job.repro_files || []).some((f) => f.review_status === 'rejected' && !f.superseded) ||
                                   (job.repro_files || []).some((f) => f.source === 'customer_replacement' && f.review_status === 'awaiting_repro' && !f.superseded))) && (
                                    <div className="space-y-3">
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileUpload}
                                            className="hidden"
                                            accept=".pdf,.ai,.eps,.psd,.png,.jpg,.jpeg,.tiff,.tif"
                                        />
                                        <Button 
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploading}
                                            className="w-full"
                                            variant="outline"
                                            data-testid="upload-file-btn"
                                        >
                                            {uploading ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                                                    Uploading...
                                                </>
                                            ) : (
                                                <>
                                                    <Upload className="h-4 w-4 mr-2" />
                                                    Upload Corrected File
                                                </>
                                            )}
                                        </Button>
                                        
                                        {/* Submit for Approval Button - shown when files are uploaded */}
                                        {job.repro_files?.length > 0 && (
                                            <Button 
                                                onClick={() => {
                                                    setNewStatus('Awaiting_Approval');
                                                    setIsStatusDialogOpen(true);
                                                }}
                                                className="w-full bg-purple-600 hover:bg-purple-700"
                                                data-testid="submit-for-approval-btn"
                                            >
                                                <Send className="h-4 w-4 mr-2" />
                                                Submit for Customer Approval
                                            </Button>
                                        )}
                                    </div>
                                )}
                                
                                {/* Show status message when awaiting approval */}
                                {job.status === 'Awaiting_Approval' && (
                                    <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-700 flex items-center gap-2">
                                        <Clock className="h-4 w-4" />
                                        Awaiting customer review
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                    </div>

                    {/* Sidebar - 1 column */}
                    <div className="space-y-6">
                        {/* Assignment Info */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">Assignment</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {job.supplier_purchase_po_number && (
                                    <div>
                                        <Label className="text-xs text-slate-400">PO Number</Label>
                                        <p className="text-sm font-medium font-mono flex items-center gap-2">
                                            <FileText className="h-4 w-4 text-slate-400" />
                                            {job.supplier_purchase_po_number}
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <Label className="text-xs text-slate-400">Catalogue No.</Label>
                                    <p className="text-sm font-medium flex items-center gap-2" data-testid="assignment-catalogue-number">
                                        <FileText className="h-4 w-4 text-slate-400" />
                                        {job.catalog_number || <span className="text-slate-400 italic">Not set</span>}
                                    </p>
                                </div>
                                <div>
                                    <Label className="text-xs text-slate-400">Partner</Label>
                                    <p className="text-sm font-medium flex items-center gap-2">
                                        <Building2 className="h-4 w-4 text-slate-400" />
                                        {job.partner_name || <span className="text-slate-400">Not assigned</span>}
                                    </p>
                                    {isAdmin && !(job.history || []).some(h => h.action === 'CORRECTED_ARTWORK_UPLOADED') && job.status !== 'Completed' && (
                                        <div className="mt-2 flex flex-col gap-1.5">
                                            {job.partner_id && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                                                    onClick={handleUnassignJob}
                                                    disabled={isUnassigning || isResetting}
                                                    data-testid="unassign-job-btn"
                                                >
                                                    {isUnassigning ? 'Unassigning...' : 'Unassign Job'}
                                                </Button>
                                            )}
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-7 text-xs text-red-700 border-red-300 bg-red-50 hover:bg-red-100"
                                                onClick={handleResetToMissing}
                                                disabled={isUnassigning || isResetting}
                                                data-testid="reset-artwork-to-missing-btn"
                                                title="Cancel the artwork job entirely and start over from Missing"
                                            >
                                                {isResetting ? 'Resetting...' : 'Reset Artwork to Missing'}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                {job.assigned_to_user_name && (
                                    <div>
                                        <Label className="text-xs text-slate-400">Assigned To</Label>
                                        <p className="text-sm font-medium flex items-center gap-2">
                                            <User className="h-4 w-4 text-slate-400" />
                                            {job.assigned_to_user_name}
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <Label className="text-xs text-slate-400">Created</Label>
                                    <p className="text-sm">{formatDate(job.created_at)}</p>
                                </div>
                                <div>
                                    <Label className="text-xs text-slate-400">Last Updated</Label>
                                    <p className="text-sm">{formatDate(job.updated_at)}</p>
                                </div>
                            </CardContent>
                        </Card>

                        {/* History */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base flex items-center gap-2">
                                    <History className="h-4 w-4" />
                                    Activity
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                                    {job.history?.slice().reverse().map((entry, idx) => (
                                        <div key={idx} className="border-l-2 border-border pl-3 py-1">
                                            <p className="text-xs font-medium text-slate-700">{entry.action.replace(/_/g, ' ')}</p>
                                            <p className="text-xs text-slate-500">{entry.details}</p>
                                            <p className="text-[10px] text-slate-400 mt-1">
                                                {entry.actor_name} • {formatDate(entry.timestamp)}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                {/* Status Update Dialog */}
                {/* Admin Artwork Status Rollback Dialog */}
                <Dialog
                    open={isAdminOverrideOpen}
                    onOpenChange={(open) => {
                        setIsAdminOverrideOpen(open);
                        if (!open) {
                            setAdminOverrideFile(null);
                            setAdminOverrideStatus('');
                            setAdminOverrideComment('');
                        }
                    }}
                >
                    <DialogContent data-testid="admin-override-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <ShieldAlert className="h-5 w-5 text-amber-600" />
                                Admin: Override File Status
                            </DialogTitle>
                            <DialogDescription>
                                Manually change the review status of this file. This action is
                                logged for audit. Use it to recover from customer mistakes such
                                as accidentally approving the wrong file.
                            </DialogDescription>
                        </DialogHeader>

                        {adminOverrideFile && (
                            <div className="space-y-4 py-4">
                                <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 text-sm">
                                    <div className="flex items-center gap-2 mb-1">
                                        <FileText className="h-4 w-4 text-amber-600" />
                                        <span className="font-medium truncate">{adminOverrideFile.filename}</span>
                                    </div>
                                    <div className="text-xs text-slate-600">
                                        {adminOverrideFile.scope === 'artwork'
                                            ? 'Original customer artwork'
                                            : 'Corrected artwork (from repro partner)'}
                                        {' · '}
                                        Current status:{' '}
                                        <span className="font-medium">{adminOverrideFile.current_status}</span>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>New Status *</Label>
                                    <Select value={adminOverrideStatus} onValueChange={setAdminOverrideStatus}>
                                        <SelectTrigger data-testid="admin-override-status-select">
                                            <SelectValue placeholder="Select the new status…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="pending">Pending review (reset)</SelectItem>
                                            <SelectItem value="approved">Approved</SelectItem>
                                            <SelectItem value="rejected">Changes requested (rejected)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label>
                                        Reason / audit note{' '}
                                        {adminOverrideStatus === 'rejected' ? '*' : '(optional)'}
                                    </Label>
                                    <Textarea
                                        value={adminOverrideComment}
                                        onChange={(e) => setAdminOverrideComment(e.target.value)}
                                        placeholder="Why is this override needed? (e.g. 'Customer approved wrong file by mistake — resetting so they can re-review')"
                                        rows={3}
                                        data-testid="admin-override-comment-input"
                                    />
                                    <p className="text-xs text-slate-500">
                                        This note is written to the job history and to system logs.
                                    </p>
                                </div>

                                {adminOverrideFile.scope === 'corrected'
                                    && adminOverrideFile.current_status === 'approved'
                                    && adminOverrideStatus
                                    && adminOverrideStatus !== 'approved'
                                    && job?.status === 'Approved' && (
                                        <div className="rounded-md border border-amber-300 bg-amber-100/60 p-2.5 text-xs text-amber-800">
                                            <strong>Note:</strong> This job is currently <em>Approved</em>. Reverting this
                                            file will move the job back to <em>Awaiting_Approval</em>.
                                        </div>
                                    )}
                            </div>
                        )}

                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsAdminOverrideOpen(false)}>
                                Cancel
                            </Button>
                            <Button
                                onClick={submitAdminOverride}
                                disabled={
                                    !adminOverrideStatus
                                    || isSubmittingAdminOverride
                                    || (adminOverrideStatus === 'rejected' && !adminOverrideComment.trim())
                                }
                                data-testid="confirm-admin-override-btn"
                            >
                                {isSubmittingAdminOverride ? 'Applying…' : 'Apply override'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Reject Artwork Dialog */}
                <Dialog open={isRejectArtworkOpen} onOpenChange={(open) => {
                    setIsRejectArtworkOpen(open);
                    if (!open) { setRejectingFileId(null); setRejectingFileName(''); }
                }}>
                    <DialogContent data-testid="reject-artwork-dialog">
                        <DialogHeader>
                            <DialogTitle>
                                {rejectingFileId ? `Request new file: ${rejectingFileName}` : 'Request new files'}
                            </DialogTitle>
                            <DialogDescription>
                                Describe what needs to be corrected so the customer can upload a new version.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-4">
                            <Label>What needs to be corrected? *</Label>
                            <Textarea
                                value={rejectComment}
                                onChange={(e) => setRejectComment(e.target.value)}
                                placeholder="e.g. Bleed area missing, resolution too low, wrong colour profile..."
                                rows={4}
                                className="mt-2"
                                data-testid="reject-comment-input"
                            />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsRejectArtworkOpen(false)}>Cancel</Button>
                            <Button
                                variant="destructive"
                                onClick={handleRejectArtwork}
                                disabled={isRejectingArtwork || !rejectComment.trim()}
                                data-testid="confirm-reject-artwork-btn"
                            >
                                {isRejectingArtwork ? 'Sending...' : 'Request New File'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Superseded Files (replaced versions) — spans both artwork_files
                    (original uploads customer replaced) and repro_files (corrected
                    files replaced by a new version). Keeps a single audit surface
                    so the partner can trace what was replaced by what. */}
                {([
                    ...(job.artwork_files || []).filter((f) => f.superseded).map((f) => ({ ...f, _scope: 'artwork' })),
                    ...(job.repro_files || []).filter((f) => f.superseded).map((f) => ({ ...f, _scope: 'corrected' })),
                ]).length > 0 && (
                    <Card className="mt-4">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2 text-slate-400">
                                <Layers className="h-4 w-4" />
                                Replaced Files
                            </CardTitle>
                            <CardDescription>Previous versions that were replaced</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            {[
                                ...(job.artwork_files || []).filter((f) => f.superseded).map((f) => ({ ...f, _scope: 'artwork' })),
                                ...(job.repro_files || []).filter((f) => f.superseded).map((f) => ({ ...f, _scope: 'corrected' })),
                            ].map((file) => (
                                <div
                                    key={`${file._scope}-${file.id}`}
                                    className="flex items-center justify-between p-2 rounded border border-slate-100 bg-slate-50 opacity-60"
                                    data-testid={`superseded-${file._scope}-${file.id}`}
                                >
                                    <div className="flex items-center gap-2">
                                        <File className="h-4 w-4 text-slate-300" />
                                        <span className="text-xs text-slate-400 line-through">{file.filename}</span>
                                        {file.version > 1 && (
                                            <span className="text-[10px] text-slate-400">(v{file.version})</span>
                                        )}
                                    </div>
                                    <Badge className="bg-slate-100 text-slate-400 text-[10px]">
                                        {file._scope === 'corrected' ? 'Superseded (corrected)' : 'Superseded'}
                                    </Badge>
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                )}

                <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Update Job Status</DialogTitle>
                            <DialogDescription>
                                Change the status of this job. This will be recorded in the activity log.
                            </DialogDescription>
                        </DialogHeader>
                        
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label>New Status</Label>
                                <Select value={newStatus} onValueChange={setNewStatus}>
                                    <SelectTrigger data-testid="new-status-select">
                                        <SelectValue placeholder="Select status..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {availableTransitions.map((status) => (
                                            <SelectItem key={status} value={status}>
                                                {STATUS_CONFIG[status]?.label || status}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Comment (optional)</Label>
                                <Textarea
                                    value={statusComment}
                                    onChange={(e) => setStatusComment(e.target.value)}
                                    placeholder="Add a note about this status change..."
                                    rows={2}
                                />
                            </div>
                        </div>

                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsStatusDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button 
                                onClick={handleStatusUpdate}
                                disabled={!newStatus || isUpdatingStatus}
                                data-testid="confirm-status-btn"
                            >
                                {isUpdatingStatus ? 'Updating...' : 'Update Status'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Upload File with Comment Dialog */}
                <Dialog open={isUploadDialogOpen} onOpenChange={(open) => {
                    setIsUploadDialogOpen(open);
                    if (!open) {
                        setSelectedFile(null);
                        setUploadComment('');
                        setReplacingReproFileId(null);
                        setReplacingCustomerFileId(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                    }
                }}>
                    <DialogContent className="overflow-hidden">
                        <DialogHeader className="min-w-0">
                            <DialogTitle>
                                {replacingReproFileId ? 'Upload new version' : 'Upload Corrected Artwork'}
                            </DialogTitle>
                            <DialogDescription>
                                {replacingReproFileId
                                    ? 'This replaces the rejected corrected file in the same version chain. The previous file is moved to Previous versions and the new upload becomes the active version.'
                                    : 'Add a comment to let the customer know about the changes you made.'}
                            </DialogDescription>
                        </DialogHeader>
                        
                        <div className="space-y-4 py-4 min-w-0 w-full overflow-hidden">
                            {selectedFile && (
                                <div className="bg-background rounded-lg p-3 flex items-center gap-3 min-w-0 w-full overflow-hidden">
                                    <FileText className="h-8 w-8 text-indigo-500 flex-shrink-0" />
                                    <div className="flex-1 min-w-0 overflow-hidden">
                                        <p className="text-sm font-medium text-slate-900 truncate" title={selectedFile.name}>{selectedFile.name}</p>
                                        <p className="text-xs text-slate-500">{formatFileSize(selectedFile.size)}</p>
                                    </div>
                                </div>
                            )}
                            
                            <div className="space-y-2">
                                <Label>Comment for Customer</Label>
                                <Textarea
                                    value={uploadComment}
                                    onChange={(e) => setUploadComment(e.target.value)}
                                    placeholder="Describe the corrections made (e.g., 'Fixed bleed area on back cover, adjusted spine width to 7mm')..."
                                    rows={3}
                                    data-testid="upload-comment-input"
                                />
                                <p className="text-xs text-slate-500">This comment will be visible to the customer when they review the artwork.</p>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsUploadDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button 
                                onClick={submitFileUpload}
                                disabled={uploading}
                                data-testid="confirm-upload-btn"
                            >
                                {uploading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="h-4 w-4 mr-2" />
                                        Upload File
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Work Performed Dialog */}
                <Dialog open={isWorkDialogOpen} onOpenChange={(open) => {
                    setIsWorkDialogOpen(open);
                    if (!open) setWorkItems([]);
                }}>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <ShoppingCart className="h-5 w-5 text-amber-600" />
                                Work Performed
                            </DialogTitle>
                            <DialogDescription>
                                Select items from the price catalog. Prices are fixed and based on the partner&apos;s catalog.
                            </DialogDescription>
                        </DialogHeader>
                        
                        <div className="flex-1 overflow-y-auto py-4 space-y-4">
                            {/* Available Catalog Items - Grouped if categories exist */}
                            <div>
                                <Label className="text-sm font-medium">Available Items</Label>
                                <p className="text-xs text-slate-500 mb-2">
                                    Catalog: {catalogData.catalog?.name || 'None'} ({catalogData.catalog?.currency || 'EUR'})
                                </p>
                                <CatalogItemsList 
                                    items={catalogData.items || []}
                                    onAddItem={addWorkItem}
                                    selectedIds={workItems.map(w => w.price_item_id)}
                                />
                            </div>

                            {/* Selected Work Items */}
                            <div>
                                <Label className="text-sm font-medium">Selected Items</Label>
                                {workItems.length > 0 ? (
                                    <div className="border rounded-lg overflow-hidden mt-2">
                                        <table className="w-full text-sm">
                                            <thead className="bg-slate-100">
                                                <tr>
                                                    <th className="text-left px-3 py-2 font-medium">Item</th>
                                                    <th className="text-center px-3 py-2 font-medium w-24">Qty</th>
                                                    <th className="text-right px-3 py-2 font-medium w-24">Price</th>
                                                    <th className="text-right px-3 py-2 font-medium w-24">Total</th>
                                                    <th className="w-10"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {workItems.map((item) => (
                                                    <tr key={item.price_item_id} className="border-t">
                                                        <td className="px-3 py-2">
                                                            <span className="font-medium">{item.name}</span>
                                                            <span className="text-xs text-slate-400 ml-1">({item.sku})</span>
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <Input
                                                                type="number"
                                                                min="1"
                                                                value={item.quantity}
                                                                onChange={(e) => updateWorkItemQty(item.price_item_id, e.target.value)}
                                                                className="w-20 h-8 text-center"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2 text-right text-slate-500">
                                                            {item.cost_price?.toFixed(2)}
                                                        </td>
                                                        <td className="px-3 py-2 text-right font-medium">
                                                            {(item.cost_price * item.quantity).toFixed(2)}
                                                        </td>
                                                        <td className="px-1 py-2">
                                                            <Button 
                                                                variant="ghost" 
                                                                size="sm"
                                                                onClick={() => removeWorkItem(item.price_item_id)}
                                                                className="text-red-500 hover:text-red-700"
                                                            >
                                                                <X className="h-4 w-4" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot className="bg-amber-50">
                                                <tr className="border-t border-amber-200">
                                                    <td colSpan="3" className="px-3 py-2 text-right font-semibold">Total:</td>
                                                    <td className="px-3 py-2 text-right font-bold text-amber-700">
                                                        {workTotal.toFixed(2)} {catalogData.catalog?.currency || 'EUR'}
                                                    </td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="text-sm text-slate-400 text-center py-4 mt-2 border rounded-lg bg-background">
                                        Click on items above to add them
                                    </p>
                                )}
                            </div>
                        </div>

                        <DialogFooter className="border-t pt-4">
                            <Button variant="outline" onClick={() => setIsWorkDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button 
                                onClick={submitWorkPerformed}
                                disabled={workItems.length === 0 || isSubmittingWork}
                                className="bg-amber-600 hover:bg-amber-700"
                                data-testid="submit-work-btn"
                            >
                                {isSubmittingWork ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 className="h-4 w-4 mr-2" />
                                        Submit Work Performed
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Complete Job / Submit to Factory Dialog */}
                <Dialog open={isCompleteDialogOpen} onOpenChange={(open) => {
                    setIsCompleteDialogOpen(open);
                    if (!open) setCompletionNote('');
                }}>
                    <DialogContent data-testid="complete-job-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                                Mark as Delivered to Factory
                            </DialogTitle>
                            <DialogDescription>
                                Confirm that you have delivered the approved files to the factory. The job will move from Active to Completed.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-4">
                            {job.work_performed?.length > 0 && (
                                <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 text-sm">
                                    <div className="flex items-center justify-between font-medium text-amber-800">
                                        <span>Work Performed</span>
                                        <span>{job.work_performed_total?.toFixed(2)} {job.work_performed_currency}</span>
                                    </div>
                                    <p className="text-xs text-amber-700 mt-1">
                                        {job.work_performed.length} item{job.work_performed.length !== 1 ? 's' : ''} will be locked and recorded.
                                    </p>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="completion-note">Note to us (optional)</Label>
                                <Textarea
                                    id="completion-note"
                                    value={completionNote}
                                    onChange={(e) => setCompletionNote(e.target.value)}
                                    placeholder="e.g. Files delivered via Pressing Plant FTP, ICC profile applied, special handling notes for admin..."
                                    rows={4}
                                    data-testid="completion-note-input"
                                />
                                <p className="text-xs text-slate-500">Visible internally on the job activity log.</p>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsCompleteDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleCompleteJob}
                                disabled={isCompletingJob || !job.work_performed || job.work_performed.length === 0}
                                className="bg-emerald-600 hover:bg-emerald-700"
                                data-testid="confirm-complete-job-btn"
                            >
                                {isCompletingJob ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 className="h-4 w-4 mr-2" />
                                        Confirm Delivery & Complete
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </main>
        </div>
    );
}

// Catalog Items List with grouping support for Work Performed dialog
function CatalogItemsList({ items, onAddItem, selectedIds = [] }) {
    const [expandedCategories, setExpandedCategories] = useState({});
    
    // Group items by category
    const { groups, ungrouped, hasCategories } = useMemo(() => {
        const groupsMap = {};
        const ungroupedItems = [];
        
        items.forEach(item => {
            const cat = item.category;
            if (cat) {
                const catLower = cat.toLowerCase();
                if (!groupsMap[catLower]) {
                    groupsMap[catLower] = { name: cat, items: [] };
                }
                groupsMap[catLower].items.push(item);
            } else {
                ungroupedItems.push(item);
            }
        });
        
        const sortedGroups = Object.values(groupsMap).sort((a, b) => 
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );
        
        return { 
            groups: sortedGroups, 
            ungrouped: ungroupedItems,
            hasCategories: sortedGroups.length > 0
        };
    }, [items]);
    
    // Default expand all categories when dialog opens
    useEffect(() => {
        if (hasCategories) {
            const initial = {};
            groups.forEach(g => { initial[g.name.toLowerCase()] = true; });
            initial['__other__'] = true;
            setExpandedCategories(initial);
        }
    }, [hasCategories, groups]);
    
    const toggleCategory = (catName) => {
        setExpandedCategories(prev => ({
            ...prev,
            [catName.toLowerCase()]: !prev[catName.toLowerCase()]
        }));
    };
    
    const renderItem = (item) => {
        const isSelected = selectedIds.includes(item.id);
        return (
            <div 
                key={item.id} 
                className={`flex items-center justify-between p-2 bg-white rounded border transition-colors cursor-pointer ${
                    isSelected ? 'border-amber-400 bg-amber-50' : 'hover:border-amber-300'
                }`}
                onClick={() => onAddItem(item)}
            >
                <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium">{item.name}</span>
                    <span className="text-xs text-slate-400 ml-2">({item.sku})</span>
                    {item.description && (
                        <p className="text-xs text-slate-500 truncate">{item.description}</p>
                    )}
                </div>
                <div className="text-right flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm font-mono text-slate-600">
                        {item.cost_price?.toFixed(2)} / {item.unit}
                    </span>
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 w-7 p-0"
                        onClick={(e) => { e.stopPropagation(); onAddItem(item); }}
                    >
                        <Plus className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        );
    };
    
    if (items.length === 0) {
        return (
            <div className="border rounded-lg p-4 bg-background text-center">
                <p className="text-sm text-slate-400">No items in catalog</p>
            </div>
        );
    }
    
    // Default to grouped view if categories exist
    if (hasCategories) {
        return (
            <div className="max-h-[250px] overflow-y-auto border rounded-lg bg-background">
                {groups.map((group) => {
                    const isExpanded = expandedCategories[group.name.toLowerCase()];
                    return (
                        <div key={group.name} className="border-b last:border-b-0">
                            <button
                                onClick={() => toggleCategory(group.name)}
                                className="w-full px-3 py-2 flex items-center justify-between bg-white hover:bg-background transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    {isExpanded ? (
                                        <ChevronDown className="w-4 h-4 text-slate-400" />
                                    ) : (
                                        <ChevronRight className="w-4 h-4 text-slate-400" />
                                    )}
                                    <Layers className="w-3.5 h-3.5 text-indigo-500" />
                                    <span className="text-sm font-medium text-slate-700">{group.name}</span>
                                    <span className="text-xs text-slate-400">({group.items.length})</span>
                                </div>
                            </button>
                            {isExpanded && (
                                <div className="px-2 pb-2 space-y-1.5">
                                    {group.items.map(renderItem)}
                                </div>
                            )}
                        </div>
                    );
                })}
                
                {ungrouped.length > 0 && (
                    <div className="border-b last:border-b-0">
                        <button
                            onClick={() => toggleCategory('__other__')}
                            className="w-full px-3 py-2 flex items-center justify-between bg-white hover:bg-background transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                {expandedCategories['__other__'] ? (
                                    <ChevronDown className="w-4 h-4 text-slate-400" />
                                ) : (
                                    <ChevronRight className="w-4 h-4 text-slate-400" />
                                )}
                                <span className="text-sm font-medium text-slate-500">Other</span>
                                <span className="text-xs text-slate-400">({ungrouped.length})</span>
                            </div>
                        </button>
                        {expandedCategories['__other__'] && (
                            <div className="px-2 pb-2 space-y-1.5">
                                {ungrouped.map(renderItem)}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
    
    // Flat list when no categories
    return (
        <div className="grid grid-cols-1 gap-2 max-h-[200px] overflow-y-auto border rounded-lg p-2 bg-background">
            {items.map(renderItem)}
        </div>
    );
}
